import { FC, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../../utils/dividerBlot'
import { Delta } from 'quill/core';
import { QuillDeltaToHtmlConverter }  from 'quill-delta-to-html';
import TemplatePicker, { ITemplatePickerTemplate } from '../TemplatePicker/TemplatePicker';
import { EMAIL_FOOTER_MARKER } from '../../../utils/emailMarkers';

export interface IMailEditorProps {
  initialText: string;
  readText: (text: string) => void;
  /** Renders the "Vložit šablonu" template picker alongside the undo/redo buttons. */
  enableTemplates?: boolean;
}

interface IMinimalQuillEditor {
  getText: () => string;
  getLength: () => number;
}

/**
 * Finds the index at which to insert content when there's no active cursor
 * selection: right before the "please keep this line" footer marker (on its
 * own line), or at the end of the document if the marker isn't present.
 * Exported standalone so it can be unit tested without a real Quill instance.
 */
export function findFooterInsertIndex(editor: IMinimalQuillEditor): number {
  const text = editor.getText();
  const markerIndex = text.indexOf(EMAIL_FOOTER_MARKER);
  if (markerIndex === -1) {
    return Math.max(editor.getLength() - 1, 0);
  }
  return text.lastIndexOf('\n', markerIndex - 1) + 1;
}

const MailEditor: FC<IMailEditorProps> = ({ initialText, readText, enableTemplates = false }): JSX.Element => {
  const quillRef = useRef<any>(null);
  // Clicking into the TemplatePicker (a separate DOM element) blurs the Quill
  // editor, so `editor.getSelection()` returns null at insert time even
  // though the user had just placed their cursor somewhere. Track the last
  // non-null selection via Quill's own event so "insert at cursor" still
  // works after that blur, rather than always falling back to the footer.
  const lastSelectionRef = useRef<{ index: number; length: number } | null>(null);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      return;
    }
    const handleSelectionChange = (range: { index: number; length: number } | null) => {
      if (range) {
        lastSelectionRef.current = range;
      }
    };
    editor.on('selection-change', handleSelectionChange);
    return () => editor.off('selection-change', handleSelectionChange);
  }, []);

  const insertHtmlAtCursor = (html: string) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      return;
    }
    const range = editor.getSelection() ?? lastSelectionRef.current;
    const index = range ? range.index : findFooterInsertIndex(editor);
    const before = editor.getLength();
    // CRITICAL: source must be 'user' — see MailEditor.test.tsx for the
    // explicit verification that this is what makes the inserted content
    // actually reach `readText` (and thus the outgoing email).
    editor.clipboard.dangerouslyPasteHTML(index, html, 'user');
    editor.setSelection(index + (editor.getLength() - before), 0, 'silent');
  };

  const handleTemplateSelect = (template: ITemplatePickerTemplate) => {
    // Mirrors the backend's own composition in SurveysService.getPrefilledEmails:
    // message + `<p>${add_message}</p>`, only when add_message is non-empty.
    const addMessage = template.addMessage?.trim();
    const html = addMessage ? `${template.message}<p>${template.addMessage}</p>` : template.message;
    insertHtmlAtCursor(html);
  };

  const handleEditorChange = (content: string, delta: Delta, source: string, editor: any) => {
    const cfg = { encodeHtml: true, paragraphTag: 'p', multiLineParagraph: false, linkTarget: ''  }
    const deltaOps = editor.getContents();
    const converter = new QuillDeltaToHtmlConverter(deltaOps.ops, cfg);
    const htmlContent = converter.convert();
    readText(htmlContent);
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && quillRef.current) {
      const editor = quillRef.current.getEditor();
      const clipboard = editor.getModule('clipboard');
 
      clipboard.addMatcher('HR', function(node: any, delta: any) {
        return new Delta().insert('---------------------------------------\n');
      });

      const delta = editor.clipboard.convert(initialText);
      editor.setContents(delta, 'silent');
    }
  }, [initialText]);

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-2" style={{ marginBottom: '10px' }}>
        <button
          onClick={() => quillRef.current?.getEditor().history.undo()}
          type='button'
        >
          🔙 Zpět
        </button>
        <button
          onClick={() => quillRef.current?.getEditor().history.redo()}
          type='button'
        >
          🔜 Opakovat
        </button>
        {enableTemplates && (
          <div style={{ minWidth: '220px' }}>
            <TemplatePicker onSelect={handleTemplateSelect} />
          </div>
        )}
      </div>
      <ReactQuill
        ref={quillRef}
        defaultValue={initialText}
        onChange={handleEditorChange}
        modules={{
          toolbar: [
            [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['bold', 'italic', 'underline'],
            ['link'],
            [{ 'align': [] }],
          ],
        }}
        formats={[
          'header', 'font',
          'list', 'bullet',
          'bold', 'italic', 'underline',
          'link', 'image', 'align',
          'divider',
        ]}
        style={{ height: '500px' }}
      />
    </div>
  );
};

export default MailEditor;